import * as React from "react";

// One agent run drawn as an instruction-trace strip: one cell per step,
// opcode-style glyphs, empty cells out to the step cap. Several of these sit
// in a post, so each is a compact row rather than a full widget.
//
// Props match the MDX registry entry:
// `steps`   one char per step: s = search/read, p = run_python, t = run_tests,
//           e = edit_file, S = submit (whitespace ignored, anything else "?")
// `label`   run name shown on the left
// `outcome` "passed" | "failed" (trimmed, case-insensitive)
// `cap`     step cap (clamped to 200; non-positive = no cap known); the strip
//           has max(cap, steps.length) cells, wrapped at 40 per row
// `showKey` optional, default true: draw the swatch key under the strip. Set
//           false on all but the last strip of a set so the key shows once.

export interface StepStripProps {
  steps: string;
  label: string;
  outcome: string;
  cap: number;
  showKey?: boolean;
}

const MAX_CAP = 200;
const DESKTOP_COLS = 40;
const MOBILE_COLS = 20;

const KIND_CLASS: Record<string, string> = {
  s: "read",
  p: "py",
  t: "test",
  e: "edit",
  S: "submit",
  "?": "unk",
};

const GLYPH: Record<string, string> = { s: "·", p: "p", t: "t", e: "e", S: "★", "?": "?" };

const KIND_NAME: Record<string, string> = {
  s: "search",
  p: "run_python",
  t: "run_tests",
  e: "edit_file",
  S: "submit",
  "?": "unknown",
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

// "search x3, run_python, edit_file" run-length description.
function describeSequence(chars: string[]): string {
  const parts: string[] = [];
  for (let i = 0; i < chars.length; ) {
    let j = i;
    while (j < chars.length && chars[j] === chars[i]) j++;
    const name = KIND_NAME[chars[i]!]!;
    parts.push(j - i > 1 ? `${name} x${j - i}` : name);
    i = j;
  }
  return parts.join(", ");
}

// Key entries use the real cell classes so a swatch cannot drift from a cell.
const KEY: { cls: string; glyph: string; text: string }[] = [
  { cls: "read", glyph: GLYPH.s!, text: "read/search" },
  { cls: "py", glyph: GLYPH.p!, text: "run_python" },
  { cls: "test", glyph: GLYPH.t!, text: "run_tests" },
  { cls: "edit", glyph: GLYPH.e!, text: "edit" },
  { cls: "submit", glyph: GLYPH.S!, text: "submit" },
  { cls: "empty", glyph: "", text: "unused budget" },
];

export function StepStrip({ steps, label, outcome, cap, showKey = true }: StepStripProps) {
  const descId = React.useId();
  const chars = Array.from((steps ?? "").replace(/\s+/g, "")).map((c) =>
    c in KIND_CLASS && c !== "?" ? c : "?",
  );
  const capKnown = Number.isFinite(cap) && cap > 0;
  const capN = capKnown ? Math.min(Math.floor(cap), MAX_CAP) : 0;
  const total = Math.max(capN, chars.length, 1);
  const state = (outcome ?? "").trim().toLowerCase();
  const passed = state === "passed";
  const failed = state === "failed";
  const firstEdit = chars.indexOf("e");
  const pyCount = chars.filter((c) => c === "p").length;
  const hitCap = failed && capKnown && chars.length >= capN && !chars.includes("S");

  const summary =
    `${label}: ` +
    (hitCap ? "failed at the step cap" : state || "outcome unknown") +
    `, ${plural(chars.length, "step")}, ` +
    (firstEdit >= 0 ? `first edit at step ${firstEdit + 1}` : "no edit") +
    `, ${plural(pyCount, "run_python call")}`;

  return (
    <div className="gc-widget ss-widget">
      <div className="ss-label" aria-hidden="true">
        <span className="ss-name">{label}</span>
        <span className={"ss-outcome" + (passed ? " ok" : "")}>
          {passed ? "✓ passed" : `✗ ${hitCap ? "failed at step cap" : state || "unknown"}`}
        </span>
      </div>
      <div
        className="ss-strip"
        role="img"
        aria-label={summary}
        aria-describedby={descId}
        style={{ "--dcols": Math.min(total, DESKTOP_COLS),
          "--mcols": Math.min(total, MOBILE_COLS),
          "--total": total, } as React.CSSProperties}
      >
        {Array.from({ length: total }, (_, i) => {
          const c = chars[i];
          if (c === undefined) return <span key={i} className="ss-cell empty" />;
          return (
            <span
              key={i}
              className={"ss-cell " + KIND_CLASS[c] + (hitCap && i === total - 1 ? " capped" : "")}
              style={{ "--i": i } as React.CSSProperties}
            >
              <span className="ss-glyph">{GLYPH[c]}</span>
              {i === firstEdit && <span className="ss-mark">{i + 1}</span>}
            </span>
          );
        })}
      </div>
      <span id={descId} className="viz-sr">
        Sequence: {chars.length ? describeSequence(chars) : "no steps"}
      </span>
      {showKey && (
        <div className="ss-key" aria-hidden="true">
          {KEY.map((k) => (
            <span key={k.cls} className="ss-key-item">
              <span className={"ss-cell ss-sw " + k.cls}>
                <span className="ss-glyph">{k.glyph}</span>
              </span>
              {k.text}
            </span>
          ))}
          <span className="ss-key-item">
            <span className="ss-sw-mark">▾</span>first edit
          </span>
          {capKnown && (
            <span className="ss-key-item">
              <span className="ss-cell ss-sw read capped">
                <span className="ss-glyph">{GLYPH.s}</span>
              </span>
              step cap
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default StepStrip;
